import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { BookMarked, Search, ShoppingCart, Package, Trash2, Pencil, X, Check, GitMerge, Plus } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PantrySubTabs from "@/components/PantrySubTabs";

const CATEGORIES = [
  "Frukt", "Grønnsaker", "Meieri", "Egg", "Kjøtt", "Fisk", "Grillmat", "Pålegg",
  "Bakevarer", "Mel & Bake", "Pasta & Ris", "Hermetikk", "Saus & Dressing",
  "Krydder", "Snacks", "Drikke", "Frysevarer", "Renhold", "Bad", "Annet",
];

export default function CatalogPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState("");
  const [filterCat, setFilterCat] = useState("all");
  const [sort, setSort] = useState("popular");
  const [top50Only, setTop50Only] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { id, name, category }
  const [dupsOpen, setDupsOpen] = useState(false);
  const [dupsLoading, setDupsLoading] = useState(false);
  const [dupGroups, setDupGroups] = useState([]);
  const [keepIds, setKeepIds] = useState({}); // { groupIdx: catalogId }
  const [addOpen, setAddOpen] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", category: "Annet" });
  const [addLoading, setAddLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const effectiveLimit = top50Only ? 50 : 300;
      const effectiveSort = top50Only ? "popular" : sort;
      const res = await api.getCatalog({ q: query, category: filterCat, sort: effectiveSort, limit: effectiveLimit });
      setItems(res.data);
    } catch { toast.error("Kunne ikke laste varekartotek"); }
    finally { setLoading(false); }
  }, [query, filterCat, sort, top50Only]);

  useEffect(() => {
    const t = setTimeout(load, 200); // debounce
    return () => clearTimeout(t);
  }, [load]);

  const handleAddToShopping = async (it) => {
    try { await api.catalogAddToShopping(it.id); toast.success(`${it.name} lagt i handleliste`); }
    catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };
  const handleAddToPantry = async (it) => {
    try { await api.catalogAddToPantry(it.id); toast.success(`${it.name} lagt i beholdning`); }
    catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };
  const handleDelete = async (it) => {
    if (!window.confirm(`Slette "${it.name}" fra kartoteket? Dette fjerner den globalt for alle båter.`)) return;
    try { await api.deleteCatalogEntry(it.id); toast.success("Slettet"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };
  const handleSaveEdit = async () => {
    if (!editing) return;
    try {
      await api.updateCatalogEntry(editing.id, { name: editing.name, category: editing.category });
      toast.success("Oppdatert");
      setEditing(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  const openDuplicates = async () => {
    setDupsOpen(true);
    setDupsLoading(true);
    try {
      const res = await api.findCatalogDuplicates();
      setDupGroups(res.data.groups || []);
      // Default keep = first item (highest count)
      const ki = {};
      (res.data.groups || []).forEach((g, i) => { ki[i] = g[0]?.id; });
      setKeepIds(ki);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil");
    } finally {
      setDupsLoading(false);
    }
  };

  const handleMergeGroup = async (groupIdx) => {
    const group = dupGroups[groupIdx];
    const keepId = keepIds[groupIdx];
    if (!group || !keepId) return;
    const mergeIds = group.filter((g) => g.id !== keepId).map((g) => g.id);
    const keepItem = group.find((g) => g.id === keepId);
    if (!window.confirm(`Slå sammen ${mergeIds.length} vare(r) inn i "${keepItem.name}"? Kan ikke angres.`)) return;
    try {
      const res = await api.mergeCatalogEntries(keepId, mergeIds);
      toast.success(`${res.data.merged} vare(r) slått sammen. ${res.data.pantry_renamed} beholdnings-oppføringer oppdatert.`);
      // Remove this group
      setDupGroups((gs) => gs.filter((_, i) => i !== groupIdx));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil");
    }
  };

  const handleAddItem = async () => {
    const name = (newItem.name || "").trim();
    if (!name) { toast.error("Skriv inn et navn"); return; }
    setAddLoading(true);
    try {
      await api.createCatalogEntry({ name, category: newItem.category });
      toast.success(`${name} lagt til i kartoteket`);
      setAddOpen(false);
      setNewItem({ name: "", category: "Annet" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil");
    } finally {
      setAddLoading(false);
    }
  };

  // Group by category for display
  const grouped = items.reduce((acc, it) => {
    const cat = it.category || "Annet";
    (acc[cat] = acc[cat] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="space-y-6 max-w-6xl" data-testid="catalog-page">
      <PantrySubTabs />
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <BookMarked className="h-7 w-7 text-navy-800" />
          <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Varekartotek
          </h1>
          <button
            onClick={() => setAddOpen(true)}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-navy-800 text-white px-3 py-1.5 text-sm font-medium hover:bg-navy-900 transition-colors"
            data-testid="add-catalog-item-btn"
          >
            <Plus className="h-4 w-4" /> Legg til vare
          </button>
          {isAdmin && (
            <button
              onClick={openDuplicates}
              className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 text-white px-3 py-1.5 text-sm font-medium hover:bg-amber-700 transition-colors"
              data-testid="find-duplicates-btn"
              title="Finn varer som trolig er duplikater (fuzzy match)"
            >
              <GitMerge className="h-4 w-4" /> Finn duplikater
            </button>
          )}
        </div>
        <p className="text-sm text-stone-500 mt-1">Alle varer brukt i appen – søk, filtrer og legg rett i handleliste eller beholdning</p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative w-full sm:flex-1 sm:min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <input
            type="text"
            placeholder="Søk vare..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-10 pr-3 py-2 rounded-lg border border-stone-200 bg-white text-sm outline-none focus:border-seawater-500"
            data-testid="catalog-search-input"
          />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-[48%] sm:w-[180px]" data-testid="catalog-category-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle kategorier</SelectItem>
            {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-[48%] sm:w-[160px]" data-testid="catalog-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="popular">Mest brukt</SelectItem>
            <SelectItem value="alpha">A-Å</SelectItem>
            <SelectItem value="recent">Sist oppdatert</SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={() => setTop50Only((v) => !v)}
          className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors ${top50Only ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-white text-stone-600 border border-stone-200 hover:bg-stone-50"}`}
          data-testid="catalog-top50-toggle"
          title="Vis kun de 50 mest brukte varene på tvers av alle båter/hjem"
        >
          🔥 Topp 50
        </button>
        <span className="text-xs text-stone-500 ml-auto">{items.length} varer</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-stone-400">Laster...</div>
      ) : items.length === 0 ? (
        <div className="text-center py-16" data-testid="empty-catalog">
          <BookMarked className="h-12 w-12 mx-auto text-stone-300 mb-4" />
          <h3 className="text-lg font-medium text-stone-600" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ingen varer ennå</h3>
          <p className="text-sm text-stone-400 mt-1">Varer registreres automatisk når du legger dem til i beholdning eller handleliste.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([category, catItems]) => (
            <div key={category} data-testid={`catalog-category-${category}`}>
              <h3 className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold mb-3">{category} <span className="text-stone-300">({catItems.length})</span></h3>
              <div className="bg-white border border-stone-100 rounded-xl divide-y divide-stone-100">
                {catItems.map((it) => (
                  <div key={it.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 hover:bg-sailcloth-100/30 transition-colors" data-testid={`catalog-item-${it.id}`}>
                    {editing?.id === it.id ? (
                      <>
                        <input
                          type="text"
                          value={editing.name}
                          onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                          className="flex-1 min-w-0 text-sm font-medium text-stone-800 rounded border border-stone-200 px-2 py-1 outline-none focus:border-seawater-500"
                          data-testid={`edit-name-${it.id}`}
                        />
                        <div className="flex items-center gap-2">
                          <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                            <SelectTrigger className="w-[140px] h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                            </SelectContent>
                          </Select>
                          <button onClick={handleSaveEdit} className="p-1.5 rounded-md bg-navy-800 text-white hover:bg-navy-900" data-testid={`save-catalog-${it.id}`}>
                            <Check className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => setEditing(null)} className="p-1.5 rounded-md text-stone-400 hover:bg-stone-100" data-testid={`cancel-catalog-${it.id}`}>
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2.5 flex-1 min-w-0">
                          {it.image_url ? (
                            <img
                              src={it.image_url}
                              alt=""
                              className="w-9 h-9 rounded-md object-cover bg-stone-100 border border-stone-200 flex-shrink-0"
                              data-testid={`catalog-img-${it.id}`}
                              loading="lazy"
                              onError={(e) => { e.currentTarget.style.display = "none"; }}
                            />
                          ) : (
                            <div className="w-9 h-9 rounded-md bg-stone-100 border border-stone-200 flex items-center justify-center flex-shrink-0" data-testid={`catalog-img-placeholder-${it.id}`}>
                              <Package className="h-4 w-4 text-stone-300" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <span className="text-sm font-medium text-stone-800 break-words">{it.name}</span>
                            <span className="ml-2 text-xs text-stone-400">brukt {it.count}×</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap sm:flex-nowrap shrink-0">
                          <button onClick={() => handleAddToShopping(it)} className="inline-flex items-center gap-1 rounded-md bg-navy-800 text-white px-2.5 py-1.5 text-xs font-semibold hover:bg-navy-900 transition-colors" data-testid={`add-shop-${it.id}`} title="Legg i handleliste">
                            <ShoppingCart className="h-3 w-3" /> <span className="hidden xs:inline">Handle</span>
                          </button>
                          <button onClick={() => handleAddToPantry(it)} className="inline-flex items-center gap-1 rounded-md border border-stone-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-stone-700 hover:bg-sailcloth-100 transition-colors" data-testid={`add-pantry-${it.id}`} title="Legg i beholdning">
                            <Package className="h-3 w-3" /> <span className="hidden xs:inline">Beholdning</span>
                          </button>
                          {isAdmin && (
                            <>
                              <button onClick={() => setEditing({ id: it.id, name: it.name, category: it.category })} className="p-1.5 rounded-md text-stone-400 hover:text-navy-800 hover:bg-stone-100 transition-colors" data-testid={`edit-catalog-${it.id}`} title="Rediger">
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => handleDelete(it)} className="p-1.5 rounded-md text-stone-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-testid={`delete-catalog-${it.id}`} title="Slett (admin)">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add new item dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-sm" data-testid="add-catalog-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Legg til vare</DialogTitle>
            <DialogDescription>Ny vare blir tilgjengelig i Varekartoteket for alle båter.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <label className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Navn</label>
              <input
                type="text"
                autoFocus
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter" && !addLoading) handleAddItem(); }}
                placeholder="f.eks. Kyllingfilet"
                className="mt-1 w-full rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-seawater-500"
                data-testid="add-catalog-name"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-stone-400 font-semibold">Kategori</label>
              <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                <SelectTrigger className="mt-1 w-full" data-testid="add-catalog-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => setAddOpen(false)}
                className="flex-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
                data-testid="cancel-add-catalog"
              >Avbryt</button>
              <button
                onClick={handleAddItem}
                disabled={addLoading || !newItem.name.trim()}
                className="flex-1 rounded-md bg-navy-800 text-white px-3 py-2 text-sm font-semibold hover:bg-navy-900 disabled:opacity-50"
                data-testid="confirm-add-catalog"
              >{addLoading ? "Lagrer..." : "Legg til"}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicates dialog (admin) */}
      <Dialog open={dupsOpen} onOpenChange={setDupsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="duplicates-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Finn & slå sammen duplikater</DialogTitle>
            <DialogDescription>
              Varer som ligner (fuzzy match). Velg hvilken som skal beholdes i hver gruppe, og klikk "Slå sammen". Andre varenavn fjernes og deres antall legges til hovedvaren. Beholdnings-oppføringer oppdateres automatisk.
            </DialogDescription>
          </DialogHeader>
          {dupsLoading ? (
            <div className="text-center py-8 text-stone-400">Søker etter duplikater...</div>
          ) : dupGroups.length === 0 ? (
            <div className="text-center py-8 text-stone-500" data-testid="no-duplicates">
              <Check className="h-10 w-10 mx-auto text-emerald-500 mb-2" />
              Ingen sannsynlige duplikater funnet!
            </div>
          ) : (
            <div className="space-y-4">
              {dupGroups.map((group, gi) => (
                <div key={`dup-${gi}-${group[0]?.id || ""}`} className="rounded-xl border border-stone-200 bg-stone-50 p-3" data-testid={`dup-group-${gi}`}>
                  <div className="space-y-1 mb-2">
                    {group.map((g) => (
                      <label key={g.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white rounded px-2 py-1 transition-colors">
                        <input
                          type="radio"
                          name={`keep-${gi}`}
                          checked={keepIds[gi] === g.id}
                          onChange={() => setKeepIds((k) => ({ ...k, [gi]: g.id }))}
                          data-testid={`keep-${gi}-${g.id}`}
                        />
                        <span className={`flex-1 ${keepIds[gi] === g.id ? "font-semibold text-navy-800" : "text-stone-700"}`}>
                          {g.name}
                        </span>
                        <span className="text-xs text-stone-400">{g.category} · {g.count}×</span>
                      </label>
                    ))}
                  </div>
                  <button
                    onClick={() => handleMergeGroup(gi)}
                    disabled={!keepIds[gi]}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-amber-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50"
                    data-testid={`merge-group-${gi}`}
                  >
                    <GitMerge className="h-3.5 w-3.5" /> Slå sammen inn i markert vare
                  </button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
